import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { api, API_BASE, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingDown, TrendingUp, ArrowRight, Download, ArrowLeftRight, Info, PackageMinus, Sparkles, Layers } from "lucide-react";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";

const EUR = (v) => (v == null ? "—" : v.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const EUR_SHORT = (v) => {
    if (v == null) return "—";
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mio. €`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Tsd. €`;
    return EUR(v);
};

const KAT_META = {
    neuerscheinung: { label: "Neuerscheinung (NE)", color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/40", icon: Sparkles },
    nachdruck:      { label: "Nachdruck (ND)",       color: "text-blue-300",    bg: "bg-blue-500/10 border-blue-500/40",       icon: Layers },
    bestand:        { label: "Aus Vorjahr weitergeführt", color: "text-slate-300", bg: "bg-slate-500/10 border-slate-500/40",   icon: ArrowLeftRight },
    abverkauft:     { label: "Komplett abverkauft",  color: "text-red-300",     bg: "bg-red-500/10 border-red-500/40",         icon: PackageMinus },
    sonstiger_zugang: { label: "Sonstiger Zugang",   color: "text-yellow-300",  bg: "bg-yellow-500/10 border-yellow-500/40",   icon: Info },
};

export default function RollForward() {
    const { activeTenant } = useTenant();
    const [jahr, setJahr] = useState("2025");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", title: "" });

    useEffect(() => {
        if (!activeTenant) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/reports/rollforward", { params: { tenant_id: activeTenant.tenant_id, jahr } });
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || "Roll-Forward konnte nicht geladen werden");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeTenant, jahr]);

    const downloadExcel = async () => {
        if (!activeTenant) return;
        try {
            const url = `${API_BASE}/reports/rollforward.xlsx?tenant_id=${activeTenant.tenant_id}&jahr=${jahr}`;
            const token = getToken();
            const resp = await fetch(url, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) throw new Error("Export fehlgeschlagen");
            const blob = await resp.blob();
            const objUrl = URL.createObjectURL(blob);
            setDlDialog({
                open: true,
                url: objUrl,
                filename: `rollforward_${activeTenant.tenant_id}_${jahr}.xlsx`,
                title: `Roll-Forward ${jahr} als Excel herunterladen`,
            });
        } catch (e) {
            toast.error(e?.message || "Excel-Export fehlgeschlagen");
        }
    };

    const s = data?.summen;

    // Waterfall-Balken: relative Breite gegen den größten Wert
    const maxWert = useMemo(() => {
        if (!s) return 0;
        return Math.max(
            Math.abs(s.anfangsbestand), Math.abs(s.endbestand),
            Math.abs(s.neuerscheinungen), Math.abs(s.nachdrucke),
            Math.abs(s.verbrauch), Math.abs(s.sonstiger_zugang),
        ) || 1;
    }, [s]);

    // Kategorien-Zusammenfassung fürs Detail (Zeilenanzahl pro Kategorie)
    const katSummary = useMemo(() => {
        if (!data?.detail) return {};
        const acc = {};
        data.detail.forEach((r) => {
            const k = r.kategorie;
            acc[k] = acc[k] || { count: 0, zugang: 0, verbrauch: 0 };
            acc[k].count += 1;
            acc[k].zugang += r.wert_zugang || 0;
            acc[k].verbrauch += r.wert_verbrauch || 0;
        });
        return acc;
    }, [data]);

    return (
        <div className="pb-16 animate-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bestandsanalyse</div>
                    <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold mt-1">
                        Bestands-<span className="text-tenant">Roll-Forward</span>
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                        Zeigt, wie sich das Lager vom Vorjahr auf das Bewertungsjahr entwickelt hat:
                        Anfangsbestand + Zugänge (Neuerscheinungen, Nachdrucke) − Verbrauch = Endbestand.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Tabs value={jahr} onValueChange={setJahr}>
                        <TabsList data-testid="rollforward-year-tabs">
                            {["2024", "2025", "2026"].map((y) => (
                                <TabsTrigger key={y} value={y}>{y}</TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    <Button
                        onClick={downloadExcel}
                        disabled={loading || !data}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="rollforward-xlsx-btn"
                    >
                        <Download className="h-4 w-4 mr-2" /> Excel
                    </Button>
                </div>
            </div>

            {loading && !data && (
                <div className="text-sm text-muted-foreground py-12 text-center">Lade Analyse …</div>
            )}

            {s && (
                <>
                    {/* KPI-Reihe */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <Card className="p-4 bg-surface1 border-border" data-testid="kpi-anfangsbestand">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Anfangsbestand {data.vorjahr}</div>
                            <div className="mt-1 text-xl font-semibold tabular-nums">{EUR_SHORT(s.anfangsbestand)}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{s.anfangsbestand_titel.toLocaleString("de-DE")} Titel</div>
                        </Card>
                        <Card className="p-4 bg-surface1 border-border" data-testid="kpi-verbrauch">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Verbrauch aus Altbestand</div>
                            <div className="mt-1 text-xl font-semibold tabular-nums text-tenant flex items-center gap-1.5">
                                <TrendingDown className="h-4 w-4" /> {EUR_SHORT(s.verbrauch)}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                {s.anfangsbestand > 0 ? `${(s.verbrauch / s.anfangsbestand * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 })} % vom Vorjahres-Lager` : "—"}
                            </div>
                        </Card>
                        <Card className="p-4 bg-surface1 border-border" data-testid="kpi-zugang">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Zugang gesamt</div>
                            <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-300 flex items-center gap-1.5">
                                <TrendingUp className="h-4 w-4" /> {EUR_SHORT(s.neuerscheinungen + s.nachdrucke + s.sonstiger_zugang)}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">NE + ND + Sonstige</div>
                        </Card>
                        <Card className="p-4 bg-surface1 border-border" data-testid="kpi-endbestand">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Endbestand {data.jahr}</div>
                            <div className="mt-1 text-xl font-semibold tabular-nums">{EUR_SHORT(s.endbestand)}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{s.endbestand_titel.toLocaleString("de-DE")} Titel</div>
                        </Card>
                    </div>

                    {/* Waterfall */}
                    <Card className="p-6 bg-surface1 border-border mb-6">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            <ArrowLeftRight className="h-3.5 w-3.5" /> Waterfall {data.vorjahr} → {data.jahr}
                        </div>
                        <div className="mt-5 space-y-3">
                            <WaterfallBar label={`Anfangsbestand ${data.vorjahr}`} value={s.anfangsbestand} max={maxWert} tone="neutral" />
                            <WaterfallBar label="+ Neuerscheinungen (NE)" value={s.neuerscheinungen} max={maxWert} tone="pos" />
                            <WaterfallBar label="+ Nachdrucke (ND)" value={s.nachdrucke} max={maxWert} tone="pos" />
                            {s.sonstiger_zugang > 0 && (
                                <WaterfallBar label="+ Sonstiger Zugang" value={s.sonstiger_zugang} max={maxWert} tone="pos" />
                            )}
                            <WaterfallBar label="− Verbrauch aus Altbestand" value={-s.verbrauch} max={maxWert} tone="neg" />
                            <WaterfallBar label={`= Endbestand ${data.jahr}`} value={s.endbestand} max={maxWert} tone="highlight" />
                        </div>
                        {Math.abs(s.kontrolldifferenz) > 1 && (
                            <div className="mt-4 text-[11px] text-muted-foreground flex items-start gap-2 border-t border-border pt-3">
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    Kontrolldifferenz: {EUR(s.kontrolldifferenz)}
                                    {" "}— entsteht durch HK-Veränderungen zwischen den Jahren (Vorjahres-Bewertung mit altem HK, aktuelle mit neuem). Für die Aussage der Waterfall unerheblich.
                                </span>
                            </div>
                        )}
                    </Card>

                    {/* Kategorien-Zusammenfassung */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                        {Object.entries(KAT_META).map(([k, meta]) => {
                            const stat = katSummary[k] || { count: 0, zugang: 0, verbrauch: 0 };
                            if (stat.count === 0) return null;
                            const Icon = meta.icon;
                            return (
                                <Card key={k} className={`p-4 border ${meta.bg}`} data-testid={`kat-summary-${k}`}>
                                    <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${meta.color}`}>
                                        <Icon className="h-3 w-3" /> {meta.label}
                                    </div>
                                    <div className="text-sm font-medium mt-1">{stat.count} Titel</div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {stat.zugang > 0 && <>Zugang: <span className="text-foreground">{EUR_SHORT(stat.zugang)}</span></>}
                                        {stat.verbrauch > 0 && <>Verbrauch: <span className="text-foreground">{EUR_SHORT(stat.verbrauch)}</span></>}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Top-Titel-Preview (nur die interessanten Kategorien) */}
                    <Card className="p-6 bg-surface1 border-border">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Detail-Vorschau</div>
                                <h3 className="font-heading text-xl font-semibold mt-1">Top-Titel je Kategorie</h3>
                                <p className="text-xs text-muted-foreground mt-1">Vollständige Liste im Excel-Export.</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {["neuerscheinung", "nachdruck", "abverkauft"].map((k) => {
                                const rows = (data.detail || []).filter((r) => r.kategorie === k).slice(0, 5);
                                if (rows.length === 0) return null;
                                const meta = KAT_META[k];
                                const Icon = meta.icon;
                                return (
                                    <div key={k}>
                                        <div className={`flex items-center gap-1.5 text-xs font-medium ${meta.color} mb-2`}>
                                            <Icon className="h-3.5 w-3.5" /> {meta.label}
                                        </div>
                                        <div className="rounded-md border border-border bg-surface2 divide-y divide-border">
                                            {rows.map((r) => (
                                                <div key={r.isbn} className="flex items-center gap-3 px-3 py-2 text-xs">
                                                    <span className="font-mono text-muted-foreground w-32 shrink-0">{r.isbn}</span>
                                                    <span className="flex-1 truncate">{r.haupttitel}</span>
                                                    <Badge variant="outline" className="text-[10px] shrink-0">{r.erloesgruppe || "—"}</Badge>
                                                    <span className="tabular-nums w-28 text-right">
                                                        {k === "abverkauft" ? EUR(r.wert_verbrauch) : EUR(r.wert_zugang)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <p className="mt-6 text-[11px] text-muted-foreground max-w-3xl">
                        Bewertungsgrundlage: Lagerwert (Bestand × verwendeter HK des Bewertungsjahres).
                        Der Verbrauch wird als Restgröße ermittelt (Anfang + Zugänge − Endstand) und enthält
                        Verkäufe, Retouren, Verramschung, Makulatur und ggf. Fehlbestand.
                    </p>
                </>
            )}
            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(o) => setDlDialog((d) => ({ ...d, open: o }))}
                url={dlDialog.url}
                filename={dlDialog.filename}
                title={dlDialog.title}
            />
        </div>
    );
}

function WaterfallBar({ label, value, max, tone }) {
    const pct = Math.min(100, (Math.abs(value) / max) * 100);
    const barColor = {
        pos: "bg-emerald-500/70",
        neg: "bg-red-500/70",
        highlight: "bg-tenant",
        neutral: "bg-slate-500/70",
    }[tone];
    const valColor = tone === "neg" ? "text-red-300" : tone === "pos" ? "text-emerald-300" : "";
    return (
        <div className="flex items-center gap-3">
            <div className="w-64 shrink-0 text-xs text-muted-foreground">{label}</div>
            <div className="flex-1 bg-surface2 rounded h-4 overflow-hidden">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <div className={`w-32 text-right text-xs tabular-nums font-medium ${valColor}`}>{EUR_SHORT(value)}</div>
        </div>
    );
}
